// No TS, just plain JS helpers

export function connectKey(brandSettingsId, platform) {
  return `social:ctx:${brandSettingsId || 'new'}:${String(platform || '').toLowerCase()}`;
}

export function lastKey(brandSettingsId, platform) {
  return `social:last:${brandSettingsId || 'new'}:${String(platform || '').toLowerCase()}`;
}

export function saveConnectCtx(ctx) {
  try {
    const key = connectKey(ctx.brandSettingsId, ctx.platform);
    sessionStorage.setItem(key, JSON.stringify({
      brandId: ctx.brandId || null,
      brandSettingsId: ctx.brandSettingsId || null,
      platform: String(ctx.platform || '').toLowerCase()
    }));
  } catch (e) {}
}

export function readConnectCtx(brandSettingsId, platform) {
  try {
    const key = connectKey(brandSettingsId, platform);
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

export function clearConnectCtx(brandSettingsId, platform) {
  try {
    sessionStorage.removeItem(connectKey(brandSettingsId, platform));
  } catch (e) {}
}

/**
 * Build a source URL to the native platform for a social target.
 * Returns null when the platform/target doesn't have a public page linkable by id.
 */
export function getTargetSourceUrl(platform, targetType, externalId, name, handle) {
  const id = externalId || '';
  const uname = (handle || '').trim();
  if (!id && !name && !uname) return null;

  switch (platform) {
    case 'facebook':
      // A page's numeric id links reliably via profile.php?id= (works for pages
      // with AND without a vanity username). Bare facebook.com/<id> does NOT
      // resolve for many pages (e.g. no-vanity pages) — it 404s / mis-routes.
      return id ? `https://www.facebook.com/profile.php?id=${id}` : null;
    case 'instagram':
      // Instagram uses usernames. The business numeric ID isn't directly
      // linkable. If the name looks like a username, use it.
      if (name && /^[a-zA-Z0-9_.]{1,30}$/.test(name) && !/^\d+$/.test(name))
        return `https://www.instagram.com/${name}`;
      return null;
    case 'linkedin': {
      // Public LinkedIn URLs resolve only from a numeric org id or a clean
      // vanity slug; an opaque/encoded id (e.g. 'QO__VQQF0G') 404s → no link.
      const vanity = /^[a-z0-9][a-z0-9-]{1,98}$/i.test(uname) ? uname : '';
      const isOrg = targetType && (targetType.includes('organization') || targetType.startsWith('li_'));
      if (isOrg) {
        if (vanity) return `https://www.linkedin.com/company/${vanity}`;
        if (/^\d+$/.test(id)) return `https://www.linkedin.com/company/${id}`;
        return null;
      }
      if (vanity) return `https://www.linkedin.com/in/${vanity}`;
      return id ? `https://www.linkedin.com/in/${id}` : null;
    }
    case 'twitter':
      return id ? `https://x.com/intent/user?user_id=${id}` : null;
    case 'youtube':
      return id && id.startsWith('UC')
        ? `https://www.youtube.com/channel/${id}`
        : null;
    case 'pinterest':
      // Pinterest profiles live at pinterest.com/<username>. The target id is a
      // BOARD id (not linkable to the profile), so pin/<id> was doubly wrong.
      // Use the account username (socialHandle) threaded in as `handle`.
      return uname ? `https://www.pinterest.com/${uname}` : null;
    case 'tiktok':
      // The `name` from TikTok's /v2/user/info is the display_name which is
      // almost always the @username. Valid TikTok usernames: a-z, 0-9, _, .
      // (no spaces, no capitals in the URL — display names can have spaces
      // but those aren't valid usernames and we can't link them).
      if (name && /^[a-zA-Z0-9_.]{2,24}$/.test(name))
        return `https://www.tiktok.com/@${name}`;
      return null;
    case 'snapchat':
      // Snapchat Public Profiles ARE linkable at snapchat.com/add/{username}.
      // The `name` from the Public Profile API is the display_name which is
      // often the same as the username for brand profiles. Spaces in the
      // name mean it's definitely NOT a username — skip those.
      if (name && /^[a-zA-Z0-9_.-]{1,30}$/.test(name) && !/^\d+$/.test(name))
        return `https://www.snapchat.com/add/${name}`;
      return null;
    case 'google':
      return null;
    default:
      return null;
  }
}
